import { ComposeClient } from "@composedb/client";
import { EthereumWebAuth, getAccountId } from "@didtools/pkh-ethereum";
import { Web3Provider } from "@ethersproject/providers";
import { DIDSession } from "did-session";
import React, { useContext, useEffect, useState } from "react";
import {
  createClient,
  useAccount,
  useConnect,
  useDisconnect,
  WagmiConfig,
} from "wagmi";
import { InjectedConnector } from "wagmi/connectors/injected";

import { CeramicContext } from "./context";

/**
 * Either load a DID session from local storage or create a new one.
 *
 * @param {EthereumAuthProvider} authProvider - The auth provider
 * @returns {DIDSession} - User's DID session
 */
const loadSession = async (
  // @ts-ignore
  authProvider: EthereumAuthProvider,
  compose: ComposeClient
): Promise<DIDSession> => {
  const sessionStr = localStorage.getItem("didsession");
  let session;

  if (sessionStr) {
    session = await DIDSession.fromSession(sessionStr);
  }

  const threeMonths = 60 * 60 * 24 * 31 * 3;
  if (!session || (session.hasSession && session.isExpired)) {
    session = await DIDSession.authorize(authProvider, {
      resources: compose.resources,
      expiresInSecs: threeMonths,
    });
    localStorage.setItem("didsession", session.serialize());
  }

  return session;
};

export function CeramicProviderInner({
  client,
  children,
}: {
  client: ComposeClient;
  children: React.ReactNode;
}) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const { address, isConnected } = useAccount();
  const { connect: connectWallet } = useConnect({
    connector: new InjectedConnector(),
  });
  const { disconnect: disconnectWallet } = useDisconnect();

  const authenticateCeramic = async () => {
    if (address) {
      const ethProvider = window.ethereum;
      const addresses = await ethProvider.enable();
      const accountId = await getAccountId(ethProvider, addresses[0]);
      const authMethod = await EthereumWebAuth.getAuthMethod(
        ethProvider,
        accountId
      );

      const session = await loadSession(authMethod, client);
      client.setDID(session.did);

      // before ceramic writes, check if session is still valid, if expired, create new
      if (session.isExpired) {
        const session = await loadSession(authMethod, client);
        client.setDID(session.did);
      }

      setIsAuthenticated(true);
    }
  };

  const revokeCeramic = () => {
    localStorage.removeItem("didsession");
  };

  const logout = () => {
    disconnectWallet();
    revokeCeramic();
    setIsAuthenticated(false);
  };

  // When connected to the wallet, automatically try to authenticate Ceramic
  useEffect(() => {
    authenticateCeramic();
  }, [address, isConnected]);

  const value = {
    client,
    isAuthenticated,
    connectWallet,
    disconnectWallet,
    authenticateCeramic,
    revokeCeramic,
    address,
    isConnected,
    logout,
  };

  return (
    <CeramicContext.Provider value={value}>{children}</CeramicContext.Provider>
  );
}

export function CeramicProvider({
  client,
  children,
}: {
  client: ComposeClient;
  children: React.ReactNode;
}) {
  try {
    const wagmiClient = createClient({
      autoConnect: true,
      provider: new Web3Provider(window.ethereum, "any"),
    });

    // App component that provides initial context values
    return (
      <WagmiConfig client={wagmiClient}>
        <CeramicProviderInner client={client}>{children}</CeramicProviderInner>
      </WagmiConfig>
    );
  } catch (err) {
    if (String(err).includes("missing provider")) {
      return children;
    } else {
      throw err;
    }
  }
}

/**
 * Hook for managing authentication with Ceramic/Metamask and interacting
 * with the ceramic client.
 *
 * @returns {CeramicContextValue} - Object containing required variables
 * and functions to interact with ceramic.
 */
export default function useCeramic() {
  const context = useContext(CeramicContext);

  if (!context) {
    return {
      client: null,
      isAuthenticated: false,
      connectWallet: null,
      disconnectWallet: null,
      authenticateCeramic: null,
      revokeCeramic: null,
      address: "",
      isConnected: true,
      logout: null,
    };
  }

  return {
    client: context.client,
    isAuthenticated: context.isAuthenticated,
    connectWallet: context.connectWallet,
    disconnectWallet: context.disconnectWallet,
    authenticateCeramic: context.authenticateCeramic,
    revokeCeramic: context.revokeCeramic,
    address: context.address,
    isConnected: context.isConnected,
    logout: context.logout,
  };
}
